import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Calculator, TrendingUp, Shield, Zap, Clock, CheckCircle2, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import PriceTicker from '../components/PriceTicker';
import { Slider } from '../components/ui/slider';

const Home = () => {
  const navigate = useNavigate();
  const [btcAmount, setBtcAmount] = useState(1);
  const [ltvRatio, setLtvRatio] = useState(50);
  const [propertyValue, setPropertyValue] = useState(500000);
  const [assumableRate, setAssumableRate] = useState(3.5);
  const [btcLoanRate, setBtcLoanRate] = useState(9.95);
  const [btcPrice, setBtcPrice] = useState(94707);

  useEffect(() => {
    fetchBtcPrice();
  }, []);

  const fetchBtcPrice = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/crypto/prices`);
      const data = await response.json();
      if (data.success) {
        setBtcPrice(data.prices.BTC.price);
      }
    } catch (error) {
      console.error('Error fetching BTC price:', error);
    }
  };

  const portfolioValue = btcAmount * btcPrice;
  const btcLoanAmount = (portfolioValue * ltvRatio) / 100;
  const monthlyBtcPayment = (btcLoanAmount * (btcLoanRate / 100)) / 12;
  const monthlyMortgagePayment = (propertyValue * (assumableRate / 100)) / 12;
  const totalMonthlyPayment = monthlyBtcPayment + monthlyMortgagePayment;
  const maxAffordableAsset = btcLoanAmount;

  return (
    <div className="min-h-screen bg-[#F7F7F7]">
      <Navbar />
      
      {/* Hero Section */}
      <section className="pt-24 pb-16 px-4 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-6">
            <PriceTicker className="inline-block mb-4" />
            <div className="inline-block bg-[#E8F4FD] text-[#006AFF] px-4 py-2 rounded-full text-sm font-medium mb-6">
              Now Live: Bitcoin-Backed Asset Loans
            </div>
          </div>
          
          <div className="text-center max-w-4xl mx-auto">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-[#2A2A33] mb-6 leading-tight">
              Unlock Distressed Assets with <span className="text-[#006AFF]">Bitcoin</span>
            </h1>
            <p className="text-lg md:text-xl text-[#6B6B76] mb-8 max-w-3xl mx-auto">
              Access commercial real estate, foreclosures, aircraft, and companies using your Bitcoin or Ethereum as collateral. No selling required. Tax-efficient. Lightning fast approval.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                onClick={() => navigate('/marketplace')}
                className="bg-[#006AFF] hover:bg-[#0055CC] text-white px-8 py-6 text-lg rounded-lg shadow-lg"
                data-testid="browse-assets-btn"
              >
                Browse Assets
              </Button>
              <Button 
                onClick={() => navigate('/calculator')}
                variant="outline"
                className="border-2 border-[#006AFF] text-[#006AFF] hover:bg-[#006AFF] hover:text-white px-8 py-6 text-lg rounded-lg"
                data-testid="calculate-loan-btn"
              >
                Calculate Loan
              </Button>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-16 max-w-5xl mx-auto">
            <div className="bg-white border border-gray-200 rounded-xl p-6 text-center shadow-sm hover:shadow-md transition-shadow">
              <div className="bg-[#E8F4FD] w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <Clock className="h-8 w-8 text-[#006AFF]" />
              </div>
              <div className="text-3xl font-bold text-[#2A2A33] mb-2">24hrs</div>
              <div className="text-[#6B6B76]">Average Approval</div>
            </div>
            <div className="bg-white border border-gray-200 rounded-xl p-6 text-center shadow-sm hover:shadow-md transition-shadow">
              <div className="bg-[#E8F4FD] w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <TrendingUp className="h-8 w-8 text-[#006AFF]" />
              </div>
              <div className="text-3xl font-bold text-[#2A2A33] mb-2">70%</div>
              <div className="text-[#6B6B76]">Max LTV Ratio</div>
            </div>
            <div className="bg-white border border-gray-200 rounded-xl p-6 text-center shadow-sm hover:shadow-md transition-shadow">
              <div className="bg-[#E8F4FD] w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle2 className="h-8 w-8 text-[#006AFF]" />
              </div>
              <div className="text-3xl font-bold text-[#2A2A33] mb-2">400+</div>
              <div className="text-[#6B6B76]">Available Assets</div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 px-4 bg-[#F7F7F7]">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-[#2A2A33] text-center mb-4">Revolutionizing Distressed Asset Finance</h2>
          <p className="text-[#6B6B76] text-center mb-12 max-w-2xl mx-auto">Leverage your crypto holdings to access unique investment opportunities</p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-white border border-gray-200 rounded-xl p-8 shadow-sm hover:shadow-lg transition-shadow">
              <div className="bg-[#E8F4FD] w-14 h-14 rounded-xl flex items-center justify-center mb-6">
                <Shield className="h-7 w-7 text-[#006AFF]" />
              </div>
              <h3 className="text-xl font-bold text-[#2A2A33] mb-3">Bitcoin-Backed Liquidity</h3>
              <p className="text-[#6B6B76]">
                Use your Bitcoin or Ethereum as collateral without selling. Maintain exposure to crypto upside while accessing cash for asset investments.
              </p>
            </div>
            <div className="bg-white border border-gray-200 rounded-xl p-8 shadow-sm hover:shadow-lg transition-shadow">
              <div className="bg-[#E8F4FD] w-14 h-14 rounded-xl flex items-center justify-center mb-6">
                <TrendingUp className="h-7 w-7 text-[#006AFF]" />
              </div>
              <h3 className="text-xl font-bold text-[#2A2A33] mb-3">Distressed Asset Access</h3>
              <p className="text-[#6B6B76]">
                Access unique investment opportunities in CRE, foreclosures, aircraft, and companies at below-market valuations with favorable terms.
              </p>
            </div>
            <div className="bg-white border border-gray-200 rounded-xl p-8 shadow-sm hover:shadow-lg transition-shadow">
              <div className="bg-[#E8F4FD] w-14 h-14 rounded-xl flex items-center justify-center mb-6">
                <Zap className="h-7 w-7 text-[#006AFF]" />
              </div>
              <h3 className="text-xl font-bold text-[#2A2A33] mb-3">Dual Growth Potential</h3>
              <p className="text-[#6B6B76]">
                Benefit from both Bitcoin appreciation and distressed asset returns while maintaining tax efficiency through strategic collateralization.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Loan Calculator Section */}
      <section className="py-20 px-4 bg-white">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-[#2A2A33] mb-4">Loan Calculator</h2>
            <p className="text-[#6B6B76] text-lg">See how much you can borrow and estimate your payments</p>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div className="bg-[#F7F7F7] border border-gray-200 rounded-xl p-8">
              <h3 className="text-xl font-semibold text-[#2A2A33] mb-6">Loan Parameters</h3>
              
              <div className="space-y-6">
                <div>
                  <div className="flex justify-between mb-2">
                    <label className="text-[#6B6B76]">Live Bitcoin Price</label>
                    <span className="text-[#006AFF] font-semibold">${btcPrice.toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
                  </div>
                </div>

                <div>
                  <div className="flex justify-between mb-2">
                    <label className="text-[#6B6B76]">Number of Bitcoin</label>
                    <span className="text-[#2A2A33] font-semibold">{btcAmount} BTC</span>
                  </div>
                  <Slider 
                    value={[btcAmount]} 
                    onValueChange={(value) => setBtcAmount(value[0])}
                    min={0.1}
                    max={1000}
                    step={0.5}
                    className="my-4"
                  />
                  <div className="text-sm text-[#6B6B76]">Portfolio Value: ${portfolioValue.toLocaleString('en-US', { minimumFractionDigits: 2 })}</div>
                </div>

                <div>
                  <div className="flex justify-between mb-2">
                    <label className="text-[#6B6B76]">Loan-to-Value Ratio</label>
                    <span className="text-[#2A2A33] font-semibold">{ltvRatio}%</span>
                  </div>
                  <Slider 
                    value={[ltvRatio]} 
                    onValueChange={(value) => setLtvRatio(value[0])}
                    min={20}
                    max={150}
                    step={5}
                    className="my-4"
                  />
                </div>

                <div>
                  <div className="flex justify-between mb-2">
                    <label className="text-[#6B6B76]">Asset Value</label>
                    <span className="text-[#2A2A33] font-semibold">${propertyValue.toLocaleString()}</span>
                  </div>
                  <Slider 
                    value={[propertyValue]} 
                    onValueChange={(value) => setPropertyValue(value[0])}
                    min={50000}
                    max={100000000}
                    step={100000}
                    className="my-4"
                  />
                  <div className="text-sm text-[#6B6B76] mb-2">Range: $50K - $100M</div>
                  {propertyValue > maxAffordableAsset && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">
                      ⚠️ This asset exceeds your collateral capacity. Max affordable: ${maxAffordableAsset.toLocaleString('en-US', { maximumFractionDigits: 0 })}
                    </div>
                  )}
                  {propertyValue <= maxAffordableAsset && propertyValue > 0 && (
                    <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-sm text-green-700">
                      ✓ This asset is within your budget
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="bg-white border border-gray-200 rounded-xl p-8 shadow-sm">
              <h3 className="text-xl font-semibold text-[#2A2A33] mb-6">Loan Summary</h3>
              
              <div className="bg-[#E8F4FD] rounded-xl p-4 mb-6">
                <div className="text-sm text-[#6B6B76] mb-1">
                  <strong className="text-[#006AFF]">Your Buying Power:</strong>
                </div>
                <div className="text-xl font-bold text-[#2A2A33]">
                  Max Affordable Asset: ${maxAffordableAsset.toLocaleString('en-US', { maximumFractionDigits: 0 })}
                </div>
                <div className="text-sm text-[#6B6B76]">
                  Based on {btcAmount} BTC at {ltvRatio}% LTV
                </div>
              </div>
              
              <div className="space-y-4">
                <div className="bg-[#F7F7F7] rounded-xl p-4">
                  <div className="text-[#6B6B76] text-sm mb-1">Bitcoin Loan Amount</div>
                  <div className="text-2xl font-bold text-[#2A2A33]">${btcLoanAmount.toLocaleString('en-US', { minimumFractionDigits: 2 })}</div>
                </div>

                <div className="bg-[#F7F7F7] rounded-xl p-4">
                  <div className="text-[#6B6B76] text-sm mb-1">Available Cash</div>
                  <div className="text-2xl font-bold text-[#2A2A33]">${btcLoanAmount.toLocaleString('en-US', { minimumFractionDigits: 2 })}</div>
                </div>

                <div className="border-t border-gray-200 pt-4 space-y-3">
                  <div className="flex justify-between">
                    <span className="text-[#6B6B76]">Bitcoin Loan Payment (Monthly)</span>
                    <span className="text-[#2A2A33] font-semibold">${monthlyBtcPayment.toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-[#6B6B76]">Asset Loan Payment</span>
                    <span className="text-[#2A2A33] font-semibold">${monthlyMortgagePayment.toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
                  </div>
                  <div className="flex justify-between text-lg font-bold border-t border-gray-200 pt-3">
                    <span className="text-[#2A2A33]">Total Monthly Payment</span>
                    <span className="text-[#006AFF]">${totalMonthlyPayment.toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-2 pt-4">
                  <div className="text-center">
                    <CheckCircle2 className="h-6 w-6 text-green-500 mx-auto mb-1" />
                    <div className="text-xs text-[#6B6B76]">Tax Efficient</div>
                  </div>
                  <div className="text-center">
                    <CheckCircle2 className="h-6 w-6 text-green-500 mx-auto mb-1" />
                    <div className="text-xs text-[#6B6B76]">No Bitcoin Sale</div>
                  </div>
                  <div className="text-center">
                    <CheckCircle2 className="h-6 w-6 text-green-500 mx-auto mb-1" />
                    <div className="text-xs text-[#6B6B76]">24hr Approval</div>
                  </div>
                </div>

                <Button className="w-full bg-[#006AFF] hover:bg-[#0055CC] text-white mt-4 py-6 rounded-lg" onClick={() => navigate('/marketplace')}>
                  Browse Assets with These Numbers
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-20 px-4 bg-[#F7F7F7]">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-[#2A2A33] text-center mb-4">How It Works</h2>
          <p className="text-[#6B6B76] text-center mb-12 max-w-2xl mx-auto">Get started in four simple steps</p>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-[#006AFF] rounded-full flex items-center justify-center text-white text-2xl font-bold mx-auto mb-4 shadow-lg">1</div>
              <h3 className="text-lg font-semibold text-[#2A2A33] mb-2">Find Assets</h3>
              <p className="text-[#6B6B76] text-sm">Browse our curated selection of distressed assets across 4 categories with 400+ opportunities.</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-[#006AFF] rounded-full flex items-center justify-center text-white text-2xl font-bold mx-auto mb-4 shadow-lg">2</div>
              <h3 className="text-lg font-semibold text-[#2A2A33] mb-2">Bitcoin Assessment</h3>
              <p className="text-[#6B6B76] text-sm">Submit your Bitcoin or Ethereum portfolio for collateral evaluation and loan capacity determination.</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-[#006AFF] rounded-full flex items-center justify-center text-white text-2xl font-bold mx-auto mb-4 shadow-lg">3</div>
              <h3 className="text-lg font-semibold text-[#2A2A33] mb-2">Loan Structuring</h3>
              <p className="text-[#6B6B76] text-sm">We structure your crypto-backed loan with up to 70% LTV to cover acquisition costs.</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-[#006AFF] rounded-full flex items-center justify-center text-white text-2xl font-bold mx-auto mb-4 shadow-lg">4</div>
              <h3 className="text-lg font-semibold text-[#2A2A33] mb-2">Close & Own</h3>
              <p className="text-[#6B6B76] text-sm">Complete the transaction and take ownership of your asset with optimal financing.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Comparison Table */}
      <section className="py-20 px-4 bg-white">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-[#2A2A33] text-center mb-12">AssumeRate vs Traditional Financing</h2>
          <div className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm">
            <table className="w-full">
              <thead className="bg-[#F7F7F7]">
                <tr>
                  <th className="text-left p-4 text-[#2A2A33] font-semibold">Feature</th>
                  <th className="text-left p-4 text-[#006AFF] font-semibold">AssumeRate</th>
                  <th className="text-left p-4 text-[#6B6B76] font-semibold">Traditional Bank</th>
                </tr>
              </thead>
              <tbody className="text-[#2A2A33]">
                <tr className="border-t border-gray-200">
                  <td className="p-4">Approval Time</td>
                  <td className="p-4 text-green-600 font-semibold">24 hours</td>
                  <td className="p-4 text-[#6B6B76]">30-90 days</td>
                </tr>
                <tr className="border-t border-gray-200 bg-[#FAFAFA]">
                  <td className="p-4">Credit Checks</td>
                  <td className="p-4 text-green-600 font-semibold">Not Required</td>
                  <td className="p-4 text-[#6B6B76]">Extensive</td>
                </tr>
                <tr className="border-t border-gray-200">
                  <td className="p-4">Collateral Source</td>
                  <td className="p-4 text-green-600 font-semibold">Bitcoin/Ethereum</td>
                  <td className="p-4 text-[#6B6B76]">Cash/Liquid Assets</td>
                </tr>
                <tr className="border-t border-gray-200 bg-[#FAFAFA]">
                  <td className="p-4">Interest Rates</td>
                  <td className="p-4 text-green-600 font-semibold">Starting at 9.95%</td>
                  <td className="p-4 text-[#6B6B76]">Current Market Rates</td>
                </tr>
                <tr className="border-t border-gray-200">
                  <td className="p-4">Tax Implications</td>
                  <td className="p-4 text-green-600 font-semibold">Tax Neutral</td>
                  <td className="p-4 text-[#6B6B76]">Capital Gains on Sales</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 bg-[#006AFF]">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">Ready to Unlock Your Investment Potential?</h2>
          <p className="text-xl text-blue-100 mb-8">
            Join the future of distressed asset finance. Get pre-qualified in minutes.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              onClick={() => navigate('/signup')}
              className="bg-white text-[#006AFF] hover:bg-gray-100 px-8 py-6 text-lg rounded-lg shadow-lg font-semibold"
            >
              Get Started Now
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
            <Button 
              onClick={() => navigate('/marketplace')}
              variant="outline"
              className="border-2 border-white text-white hover:bg-white hover:text-[#006AFF] px-8 py-6 text-lg rounded-lg"
            >
              View All Assets
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Home;
